import React, { useState, useEffect } from 'react';
import { Plus, PencilSimple, Trash, GasPump, ChartBar, X, Check, UploadSimple, Spinner } from '@phosphor-icons/react';
import axios from '../../utils/axiosInstance';
import { vehicleLabel } from '../../utils/vehicleLabel';
import { WlasnoscFilter, WlasnoscBadge, normalizeWlasnosc } from '../shared/WlasnoscFilter';

const API = `${window.location.origin}/api`;

const todayStr = () => new Date().toISOString().slice(0, 10);

const emptyForm = {
  pojazd_id: '',
  data: todayStr(),
  litry: '',
  kwota: '',
  przebieg: '',
  notes: '',
};

const MONTHS_PL = [
  'styczeń', 'luty', 'marzec', 'kwiecień', 'maj', 'czerwiec',
  'lipiec', 'sierpień', 'wrzesień', 'październik', 'listopad', 'grudzień',
];

const CATEGORY_META = {
  paliwo: { label: 'Paliwo', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  myjnia: { label: 'Myjnia', cls: 'bg-violet-100 text-violet-800 border-violet-300' },
  plyn:   { label: 'Płyn',   cls: 'bg-sky-100 text-sky-800 border-sky-300' },
  inne:   { label: 'Inne',   cls: 'bg-slate-100 text-slate-700 border-slate-300' },
};

const KategoriaBadge = ({ value }) => {
  const meta = CATEGORY_META[value] || CATEGORY_META.inne;
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold border ${meta.cls}`}
      data-testid={`kategoria-badge-${value || 'inne'}`}
    >
      {meta.label}
    </span>
  );
};

export default function TankowaniaTab() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 1..12; 0 = wszystkie
  const [pojazdFilter, setPojazdFilter] = useState(''); // '' = all
  const [dateFrom, setDateFrom] = useState(''); // YYYY-MM-DD; '' = no lower bound
  const [dateTo, setDateTo] = useState('');     // YYYY-MM-DD; '' = no upper bound
  const [wlasnoscFilter, setWlasnoscFilter] = useState('all');
  const [tankowania, setTankowania] = useState([]);
  const [stats, setStats] = useState([]);
  const [pojazdy, setPojazdy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    fetchData();
    fetchPojazdy();
  }, [year, month, pojazdFilter, dateFrom, dateTo, wlasnoscFilter]);

  const fetchPojazdy = async () => {
    try {
      const res = await axios.get(`${API}/pojazdy`);
      setPojazdy(res.data || []);
    } catch (err) {
      console.error('Failed to load pojazdy', err);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [list, statsRes] = await Promise.all([
        axios.get(`${API}/tankowania`),
        axios.get(`${API}/tankowania/stats`, {
          params: month ? { year, month } : { year },
        }),
      ]);
      const filtered = (list.data || []).filter((r) => {
        if (!r.data) return false;
        // Date range filter (overrides Year/Month if both bounds given)
        if (dateFrom && r.data < dateFrom) return false;
        if (dateTo && r.data > dateTo) return false;
        // Year/Month filter only applies when no explicit range
        if (!dateFrom && !dateTo) {
          const [y, m] = r.data.split('-');
          if (parseInt(y, 10) !== year) return false;
          if (month && parseInt(m, 10) !== month) return false;
        }
        if (pojazdFilter && r.pojazd_id !== pojazdFilter) return false;
        // Wlasnosc filter (via pojazd lookup)
        if (wlasnoscFilter !== 'all') {
          const p = pojazdy.find((x) => x.id === r.pojazd_id);
          const w = normalizeWlasnosc(p?.wlasnosc);
          if (w !== wlasnoscFilter) return false;
        }
        return true;
      });
      setTankowania(filtered);
      setStats(statsRes.data || []);
    } catch (err) {
      console.error('Failed to fetch tankowania', err);
      alert('Błąd podczas pobierania danych');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setShowForm(false);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.pojazd_id) {
      alert('Wybierz pojazd');
      return;
    }
    try {
      const payload = {
        pojazd_id: formData.pojazd_id,
        data: formData.data,
        litry: parseFloat(formData.litry) || 0,
        kwota: parseFloat(formData.kwota) || 0,
        przebieg: formData.przebieg ? parseInt(formData.przebieg, 10) : null,
        notes: formData.notes || null,
      };
      if (editingId) {
        await axios.put(`${API}/tankowania/${editingId}`, payload);
      } else {
        await axios.post(`${API}/tankowania`, payload);
      }
      resetForm();
      fetchData();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || 'Błąd zapisu');
    }
  };

  const handleEdit = (r) => {
    setEditingId(r.id);
    setFormData({
      pojazd_id: r.pojazd_id,
      data: r.data,
      litry: r.litry,
      kwota: r.kwota,
      przebieg: r.przebieg ?? '',
      notes: r.notes ?? '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Usunąć to tankowanie?')) return;
    try {
      await axios.delete(`${API}/tankowania/${id}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Błąd');
    }
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/tankowania/import-orlen`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data);
      fetchData();
    } catch (err) {
      console.error('Import failed:', err);
      alert(err.response?.data?.detail || 'Błąd importu');
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  const totals = tankowania.reduce(
    (acc, r) => {
      acc.litry += parseFloat(r.litry || 0);
      acc.kwota += parseFloat(r.kwota || 0);
      return acc;
    },
    { litry: 0, kwota: 0 }
  );

  return (
    <div className="space-y-6" data-testid="tankowania-tab">
      {/* Header & Filters */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 flex items-center gap-2">
            <GasPump className="w-7 h-7 text-orange-600" weight="bold" />
            Tankowania
          </h2>
          <p className="text-slate-600 text-sm mt-1">
            Rejestr tankowań i analiza zużycia paliwa
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Rok</label>
            <select
              data-testid="tankowania-year"
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Miesiąc</label>
            <select
              data-testid="tankowania-month"
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value, 10))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value={0}>Wszystkie</option>
              {MONTHS_PL.map((m, i) => (
                <option key={i + 1} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Pojazd</label>
            <select
              data-testid="tankowania-pojazd-filter"
              value={pojazdFilter}
              onChange={(e) => setPojazdFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm min-w-[180px]"
            >
              <option value="">Wszystkie pojazdy</option>
              <optgroup label="Firmowe">
                {pojazdy.filter((p) => normalizeWlasnosc(p.wlasnosc) === 'firmowy').map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nazwa}{p.nr_rejestracyjny ? ` (${p.nr_rejestracyjny})` : ''}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Prywatne">
                {pojazdy.filter((p) => normalizeWlasnosc(p.wlasnosc) === 'prywatny').map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nazwa}{p.nr_rejestracyjny ? ` (${p.nr_rejestracyjny})` : ''}
                  </option>
                ))}
              </optgroup>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Własność</label>
            <WlasnoscFilter value={wlasnoscFilter} onChange={setWlasnoscFilter} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Od (data)</label>
            <input
              data-testid="tankowania-date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Do (data)</label>
            <input
              data-testid="tankowania-date-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          {(dateFrom || dateTo) && (
            <button
              data-testid="tankowania-clear-dates"
              onClick={() => { setDateFrom(''); setDateTo(''); }}
              className="px-3 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900 border border-gray-300 rounded-lg hover:bg-slate-50"
              title="Wyczyść zakres dat"
            >
              Wyczyść daty
            </button>
          )}
          <button
            data-testid="add-tankowanie-btn"
            onClick={() => {
              if (showForm) {
                resetForm();
              } else {
                setFormData({ ...emptyForm });
                setShowForm(true);
              }
            }}
            className="px-5 py-2.5 bg-muted hover:from-orange-700 hover:to-amber-700 text-white rounded-lg font-semibold flex items-center gap-2 shadow"
          >
            <Plus className="w-5 h-5" weight="bold" />
            Dodaj tankowanie
          </button>
          <input
            type="file"
            id="orlen-import-file"
            accept=".csv"
            onChange={handleImportFile}
            className="hidden"
            data-testid="orlen-import-input"
          />
          <label
            htmlFor="orlen-import-file"
            data-testid="orlen-import-btn"
            className={`px-5 py-2.5 rounded-lg font-semibold flex items-center gap-2 cursor-pointer transition-all ${
              importing
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-foreground hover:from-blue-700 hover:to-indigo-700 text-white shadow'
            }`}
          >
            {importing ? (
              <>
                <Spinner className="w-5 h-5 animate-spin" weight="bold" />
                Importuję…
              </>
            ) : (
              <>
                <UploadSimple className="w-5 h-5" weight="bold" />
                Importuj z Orlen
              </>
            )}
          </label>
        </div>
      </div>

      {/* Import result banner */}
      {importResult && (
        <div
          className="bg-muted border border-border rounded-sm p-5 shadow"
          data-testid="import-result"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                <UploadSimple className="w-5 h-5 text-foreground" weight="bold" />
                Wynik importu z Orlen ({importResult.total_rows} wierszy)
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                <div className="bg-emerald-100 text-emerald-800 rounded-lg p-3">
                  <div className="text-xs uppercase font-semibold">Zaimportowano</div>
                  <div className="text-2xl font-bold">{importResult.imported}</div>
                </div>
                <div className="bg-amber-100 text-amber-800 rounded-lg p-3">
                  <div className="text-xs uppercase font-semibold">Duplikaty (pominięte)</div>
                  <div className="text-2xl font-bold">{importResult.duplicates}</div>
                </div>
                <div className="bg-red-100 text-red-800 rounded-lg p-3">
                  <div className="text-xs uppercase font-semibold">Bez przypisanego pojazdu</div>
                  <div className="text-2xl font-bold">{importResult.skipped_no_card}</div>
                </div>
              </div>
              {importResult.by_category && (
                <div
                  className="flex flex-wrap gap-2 mb-3"
                  data-testid="import-by-category"
                >
                  {Object.entries(CATEGORY_META).map(([key, meta]) => {
                    const count = importResult.by_category[key] || 0;
                    if (!count) return null;
                    return (
                      <div
                        key={key}
                        className={`px-3 py-1.5 rounded-lg border text-sm font-medium ${meta.cls}`}
                      >
                        {meta.label}: <strong>{count}</strong>
                      </div>
                    );
                  })}
                </div>
              )}
              {importResult.unmatched_cards && importResult.unmatched_cards.length > 0 && (
                <div className="bg-white border border-red-200 rounded-lg p-3">
                  <p className="text-sm font-semibold text-red-800 mb-2">
                    Karty bez przypisanego pojazdu — dodaj numer karty do pojazdu w Liście pojazdów:
                  </p>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {importResult.unmatched_cards.map((c) => (
                      <div
                        key={c.card}
                        className="flex justify-between items-center text-sm bg-red-50 px-3 py-2 rounded"
                      >
                        <span className="font-mono text-slate-700">{c.card}</span>
                        <span className="text-slate-600">
                          {c.count} tankowań ·{' '}
                          <strong>{c.kwota_total.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł</strong>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={() => setImportResult(null)}
              className="text-slate-400 hover:text-slate-600"
              title="Zamknij"
            >
              <X className="w-6 h-6" weight="bold" />
            </button>
          </div>
        </div>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="bg-muted border border-orange-200 rounded-sm p-6 shadow space-y-4"
          data-testid="tankowanie-form"
        >
          <h3 className="font-bold text-slate-800">{editingId ? 'Edytuj tankowanie' : 'Nowe tankowanie'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-1">Pojazd *</label>
              <select
                data-testid="tankowanie-pojazd"
                required
                value={formData.pojazd_id}
                onChange={(e) => setFormData({ ...formData, pojazd_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">— wybierz —</option>
                <optgroup label="Firmowe">
                  {pojazdy.filter((p) => normalizeWlasnosc(p.wlasnosc) === 'firmowy').map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nazwa}{p.nr_rejestracyjny ? ` (${p.nr_rejestracyjny})` : ''}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Prywatne">
                  {pojazdy.filter((p) => normalizeWlasnosc(p.wlasnosc) === 'prywatny').map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nazwa}{p.nr_rejestracyjny ? ` (${p.nr_rejestracyjny})` : ''}
                    </option>
                  ))}
                </optgroup>
              </select>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-1">Data *</label>
              <input
                data-testid="tankowanie-data"
                required
                type="date"
                value={formData.data}
                onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-1">Litry *</label>
              <input
                data-testid="tankowanie-litry"
                required
                type="number"
                step="0.01"
                min="0"
                value={formData.litry}
                onChange={(e) => setFormData({ ...formData, litry: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="np. 50,5"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-1">Kwota (zł) *</label>
              <input
                data-testid="tankowanie-kwota"
                required
                type="number"
                step="0.01"
                min="0"
                value={formData.kwota}
                onChange={(e) => setFormData({ ...formData, kwota: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="np. 325,00"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-1">Przebieg (km)</label>
              <input
                data-testid="tankowanie-przebieg"
                type="number"
                min="0"
                value={formData.przebieg}
                onChange={(e) => setFormData({ ...formData, przebieg: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="np. 120000"
              />
            </div>
            <div className="md:col-span-2 lg:col-span-3">
              <label className="text-sm font-semibold text-slate-700 block mb-1">Notatki</label>
              <input
                data-testid="tankowanie-notes"
                type="text"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="opcjonalnie"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="tankowanie-submit"
              type="submit"
              className="px-5 py-2.5 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-semibold flex items-center gap-2"
            >
              <Check className="w-5 h-5" weight="bold" />
              {editingId ? 'Zapisz' : 'Dodaj'}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="px-5 py-2.5 bg-gray-300 hover:bg-gray-400 text-slate-700 rounded-lg font-semibold flex items-center gap-2"
            >
              <X className="w-5 h-5" weight="bold" />
              Anuluj
            </button>
          </div>
        </form>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-muted rounded-sm p-5 text-white shadow">
          <div className="text-sm opacity-90">Tankowań</div>
          <div className="text-4xl font-bold">{tankowania.length}</div>
        </div>
        <div className="bg-foreground rounded-sm p-5 text-white shadow">
          <div className="text-sm opacity-90">Łącznie litry</div>
          <div className="text-4xl font-bold">{totals.litry.toFixed(2)} L</div>
        </div>
        <div className="bg-muted rounded-sm p-5 text-white shadow">
          <div className="text-sm opacity-90">Łącznie kwota</div>
          <div className="text-4xl font-bold">
            {totals.kwota.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
          </div>
        </div>
      </div>

      {/* Stats per pojazd */}
      {(() => {
        const filteredStats = pojazdFilter ? stats.filter((s) => s.pojazd_id === pojazdFilter) : stats;
        if (filteredStats.length === 0) return null;
        return (
        <div className="bg-white rounded-sm shadow border border-gray-100 overflow-hidden">
          <div className="bg-slate-50 px-5 py-3 border-b border-gray-200 flex items-center gap-2">
            <ChartBar className="w-5 h-5 text-slate-600" weight="bold" />
            <h3 className="font-bold text-slate-800">Statystyki na pojazd</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase text-slate-600">Pojazd</th>
                  <th className="text-right py-3 px-4 text-xs font-bold uppercase text-slate-600">Tankowań</th>
                  <th className="text-right py-3 px-4 text-xs font-bold uppercase text-slate-600">Litry</th>
                  <th className="text-right py-3 px-4 text-xs font-bold uppercase text-slate-600">Kwota</th>
                  <th className="text-right py-3 px-4 text-xs font-bold uppercase text-slate-600">Przejechano</th>
                  <th className="text-right py-3 px-4 text-xs font-bold uppercase text-slate-600">Średnie spalanie</th>
                </tr>
              </thead>
              <tbody>
                {filteredStats.map((s) => (
                  <tr key={s.pojazd_id} className="hover:bg-slate-50" data-testid={`stat-row-${s.pojazd_id}`}>
                    <td className="py-3 px-4 font-semibold text-slate-800">
                      {s.pojazd_name || '—'}
                      {s.nr_rejestracyjny && (
                        <span className="ml-2 text-xs text-slate-500 font-mono">
                          {s.nr_rejestracyjny}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right text-slate-700">{s.count}</td>
                    <td className="py-3 px-4 text-right text-slate-700">{s.litry_total.toFixed(2)} L</td>
                    <td className="py-3 px-4 text-right font-semibold text-slate-800">
                      {s.kwota_total.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                    </td>
                    <td className="py-3 px-4 text-right text-slate-700">
                      {s.distance_km ? `${s.distance_km} km` : '—'}
                    </td>
                    <td className="py-3 px-4 text-right">
                      {s.consumption_l_per_100km ? (
                        <span className="font-bold text-orange-700">
                          {s.consumption_l_per_100km.toFixed(2)} L/100km
                        </span>
                      ) : (
                        <span className="text-slate-400 text-sm">brak danych</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        );
      })()}

      {/* List of refuels */}
      <div className="bg-white rounded-sm shadow border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="text-center py-10 text-slate-500">Ładowanie…</div>
        ) : tankowania.length === 0 ? (
          <div className="text-center py-12">
            <GasPump className="w-14 h-14 text-gray-300 mx-auto mb-3" />
            <p className="text-slate-500">Brak tankowań w wybranym okresie</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="tankowania-list">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase text-slate-600">Data</th>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase text-slate-600">Pojazd</th>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase text-slate-600">Kategoria</th>
                  <th className="text-right py-3 px-4 text-xs font-bold uppercase text-slate-600">Litry</th>
                  <th className="text-right py-3 px-4 text-xs font-bold uppercase text-slate-600">Kwota</th>
                  <th className="text-right py-3 px-4 text-xs font-bold uppercase text-slate-600">Przebieg</th>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase text-slate-600">Notatki</th>
                  <th className="text-right py-3 px-4 text-xs font-bold uppercase text-slate-600">Akcje</th>
                </tr>
              </thead>
              <tbody>
                {tankowania.map((r) => {
                  const kat = r.kategoria || (r.import_source === 'orlen' ? 'inne' : 'paliwo');
                  const litryNum = parseFloat(r.litry || 0);
                  const showLitry = kat === 'paliwo' && litryNum > 0;
                  return (
                  <tr key={r.id} className="hover:bg-orange-50/40" data-testid={`tankowanie-row-${r.id}`}>
                    <td className="py-3 px-4 text-slate-700 font-mono text-sm">{r.data}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-800">{vehicleLabel(r)}</span>
                        {r.pojazd_id && (
                          <WlasnoscBadge value={pojazdy.find((p) => p.id === r.pojazd_id)?.wlasnosc} size="xs" />
                        )}
                      </div>
                      {r.nr_rejestracyjny && (
                        <div className="text-xs text-slate-500 font-mono">{r.nr_rejestracyjny}</div>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <KategoriaBadge value={kat} />
                    </td>
                    <td className="py-3 px-4 text-right text-slate-700">
                      {showLitry ? `${litryNum.toFixed(2)} L` : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="py-3 px-4 text-right font-semibold text-slate-800">
                      {parseFloat(r.kwota).toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                    </td>
                    <td className="py-3 px-4 text-right text-slate-700">
                      {r.przebieg ? `${r.przebieg} km` : '—'}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600 max-w-xs truncate">{r.notes || '—'}</td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          data-testid={`tankowanie-edit-${r.id}`}
                          onClick={() => handleEdit(r)}
                          className="p-2 hover:bg-muted text-foreground rounded-lg"
                          title="Edytuj"
                        >
                          <PencilSimple className="w-4 h-4" weight="bold" />
                        </button>
                        <button
                          data-testid={`tankowanie-delete-${r.id}`}
                          onClick={() => handleDelete(r.id)}
                          className="p-2 hover:bg-red-100 text-red-600 rounded-lg"
                          title="Usuń"
                        >
                          <Trash className="w-4 h-4" weight="bold" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
